import PropTypes from 'prop-types';
import React from 'react';

import { DetailCard, DetailRow, titleCase } from './transaction-detail-primitives';
import { buildTransactionTimeline, formatDetailDate } from './transaction-detail-utils';

const getTransactionType = (type) =>
  String(type ?? '').toUpperCase() === 'PAYMENT' ? 'Collection' : titleCase(type) ?? 'Collection';

const TransactionSummaryCard = ({ summary, status }) => {
  // `neutral` (Expired) is the default row colour, so it needs no modifier.
  const statusClassName =
    status.tone === 'neutral' ? '' : `transaction-detail__value--${status.tone}`;

  return (
    <DetailCard title="Transaction summary">
      <DetailRow label="Status" value={<span className={statusClassName}>{status.label}</span>} />
      <DetailRow label="Type" value={getTransactionType(summary.type)} />
      <DetailRow label="Description" value={summary.description} />
      <DetailRow label="Basqet transaction ID" value={summary.basqet_transaction_id} copy />
      <DetailRow label="Basqet reference" value={summary.basqet_reference} copy />
      {/* label is per-merchant: "Selar reference" or "Merchant reference" */}
      <DetailRow
        label={summary.merchant_reference_label || 'Merchant reference'}
        value={summary.merchant_reference}
        copy={Boolean(summary.merchant_reference)}
      />
      <DetailRow label="Date created" value={formatDetailDate(summary.created_at)} />
      <DetailRow label="Date completed" value={formatDetailDate(summary.completed_at)} />
    </DetailCard>
  );
};

const CustomerDetailsCard = ({ customer: maybeCustomer }) => {
  const customer = maybeCustomer || {};

  return (
    <DetailCard title="Customer details">
      <DetailRow label="Customer name" value={customer.name} />
      <DetailRow label="Customer email" value={customer.email} copy={Boolean(customer.email)} />
    </DetailCard>
  );
};

const TransactionTimelineCard = ({ timeline }) => {
  const events = buildTransactionTimeline(timeline);

  return (
    <DetailCard title="Transaction timeline">
      {events.length ? (
        <ol className="transaction-detail__timeline">
          {events.map((event, index) => (
            <li
              key={`${event.label}-${index}`}
              className={`transaction-detail__timeline-item transaction-detail__timeline-item--${event.tone}`}>
              <span className="transaction-detail__timeline-dot" />
              <div>
                <strong>{event.label}</strong>
                <span>{formatDetailDate(event.date) ?? 'Not available'}</span>
              </div>
            </li>
          ))}
        </ol>
      ) : (
        <span className="transaction-detail__value--muted">No activity recorded yet</span>
      )}
    </DetailCard>
  );
};

const TransactionDetailSidebar = ({ summary, customer, timeline, status }) => (
  <aside className="transaction-detail__sidebar">
    <TransactionSummaryCard summary={summary} status={status} />
    <CustomerDetailsCard customer={customer} />
    <TransactionTimelineCard timeline={timeline} />
  </aside>
);

const statusShape = PropTypes.shape({
  label: PropTypes.string.isRequired,
  tone: PropTypes.string.isRequired
});

TransactionSummaryCard.propTypes = {
  summary: PropTypes.object.isRequired,
  status: statusShape.isRequired
};
CustomerDetailsCard.propTypes = { customer: PropTypes.object };
TransactionTimelineCard.propTypes = { timeline: PropTypes.array };
TransactionDetailSidebar.propTypes = {
  summary: PropTypes.object.isRequired,
  customer: PropTypes.object,
  timeline: PropTypes.array,
  status: statusShape.isRequired
};

export { TransactionDetailSidebar };
